import React, { useEffect, useState, useCallback } from "react";
import cx from "classnames";
import { useEmblaCarousel } from "embla-carousel/react";
import StarRatings from "react-star-ratings";

import { ReactComponent as LeftArrow } from "../../images/svg/LeftArrow.svg";
import { ReactComponent as RightArrow } from "../../images/svg/RightArrow.svg";

import styles from "./reviews-carousel.module.css";

const DotButton = ({
  selected,
  onClick
}: {
  selected: boolean;
  onClick: () => void;
}) => (
  <button
    className={`embla__dot${selected ? " is-selected" : ""}`}
    onClick={onClick}
    aria-label="Go to page"
  />
);

const PrevButton = ({
  enabled,
  onClick
}: {
  enabled: boolean;
  onClick: () => void;
}) => (
  <button
    className="embla__button embla__button--prev"
    onClick={onClick}
    disabled={!enabled}
    aria-label="Previous page"
  >
    <LeftArrow />
  </button>
);

const NextButton = ({
  enabled,
  onClick
}: {
  enabled: boolean;
  onClick: () => void;
}) => (
  <button
    className="embla__button embla__button--next"
    onClick={onClick}
    disabled={!enabled}
    aria-label="Next page"
  >
    <RightArrow />
  </button>
);

interface ReviewsCarouselProps {
  module: {
    headline?: string;
    reviews: Array<{
      rating: number;
      quote: string;
      credit: string;
      _key: string;
    }>;
  };
}
export const ReviewsCarousel = ({ module }: ReviewsCarouselProps) => {
  const [EmblaCarousel, embla] = useEmblaCarousel({
    loop: false,
    slidesToScroll: 1,
    align: "start",
    containScroll: "trimSnaps"
  });
  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  const scrollTo = useCallback(index => embla && embla.scrollTo(index), [
    embla
  ]);
  const scrollPrev = useCallback(() => embla && embla.scrollPrev(), [embla]);
  const scrollNext = useCallback(() => embla && embla.scrollNext(), [embla]);

  const onSelect = useCallback(() => {
    if (!embla) return;
    setSelectedIndex(embla.selectedScrollSnap());
    setPrevBtnEnabled(embla.canScrollPrev());
    setNextBtnEnabled(embla.canScrollNext());
  }, [embla, setSelectedIndex]);

  useEffect(() => {
    if (!embla) return;
    onSelect();
    setScrollSnaps(embla.scrollSnapList());
    embla.on("select", onSelect);
  }, [embla, setScrollSnaps, onSelect]);

  const { headline, reviews } = module;

  return (
    <div className={cx(styles.reviewsCarousel)}>
      <h2 className={cx("small-caps-13", styles.headline)}>{headline}</h2>
      <div className={cx(styles.carouselContainer)}>
        <EmblaCarousel>
          <div style={{ display: "flex" }}>
            {reviews &&
              reviews.map(review => (
                <div key={review._key} className={styles.carouselSlide}>
                  <div className={styles.carouselSlideInner}>
                    <div>
                      <StarRatings
                        rating={review.rating}
                        starRatedColor="rgba(123, 57, 78, 1)"
                        starEmptyColor="rgba(123, 57, 78, .2)"
                        starDimension="15px"
                        starSpacing="3px"
                        numberOfStars={5}
                        name="rating"
                      />
                    </div>
                    <p className={cx("body", styles.quote)}>“{review.quote}”</p>
                    <p className={cx("body body--small", styles.credit)}>
                      {review.credit}
                    </p>
                  </div>
                </div>
              ))}
          </div>
        </EmblaCarousel>
      </div>
      <div className="embla__dots">
        {scrollSnaps.map((snap, index) => (
          <DotButton
            selected={index === selectedIndex}
            onClick={() => scrollTo(index)}
            key={index}
          />
        ))}
      </div>
      <PrevButton onClick={scrollPrev} enabled={prevBtnEnabled} />
      <NextButton onClick={scrollNext} enabled={nextBtnEnabled} />
    </div>
  );
};
